import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, FileSpreadsheet, Check, X, AlertTriangle, RefreshCw, Trash2, ChevronDown, ChevronUp, Download, Users, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResultsImport() {
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [racingMappings, setRacingMappings] = useState([]);
  const [motoMappings, setMotoMappings] = useState([]);
  const [currentResults, setCurrentResults] = useState(null);
  const [showCurrentResults, setShowCurrentResults] = useState(false);
  const [activeLeague, setActiveLeague] = useState('racing');
  const [selectedSheet, setSelectedSheet] = useState('all'); // 'all' or specific sheet name
  const [allSheets, setAllSheets] = useState([]); // List of all sheet names

  useEffect(() => {
    fetchCurrentResults();
  }, []);

  const fetchCurrentResults = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/results/current`, { withCredentials: true });
      setCurrentResults(response.data);
    } catch (error) {
      console.error('Failed to fetch current results:', error);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/results/template`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'MSF_Results_Template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Template downloaded!');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      toast.loading('Parsing Excel file...');
      const response = await axios.post(`${BACKEND_URL}/api/admin/results/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.dismiss();

      setPreviewData(response.data);
      
      // Show debug info if no data found
      if (response.data.debug_info?.length > 0 && 
          response.data.racing_data?.length === 0 && 
          response.data.moto_data?.length === 0) {
        if (process.env.NODE_ENV !== 'production') console.log('Debug info:', response.data.debug_info);
        const debugMsg = response.data.debug_info
          .map(d => `${d.sheet}: ${d.status} (header row: ${d.header_row_found || 'not found'})`)
          .join('\n');
        toast.error(`No data found. Debug:\n${debugMsg}`, { duration: 8000 });
      }
      
      // Store all sheet names for tab navigation (racing and moto separately)
      const racingSheets = response.data.racing_data?.map(s => s.sheet_name) || [];
      const motoSheets = response.data.moto_data?.map(s => s.sheet_name) || [];
      setAllSheets(racingSheets); // Default to racing sheets
      setSelectedSheet('all');
      
      // Initialize mappings from ALL racing sheets
      if (response.data.racing_data?.length > 0) {
        const allRacingData = [];
        response.data.racing_data.forEach(sheet => {
          sheet.data.forEach(item => {
            allRacingData.push({
              ...item,
              sheet_name: sheet.sheet_name,
              db_id: item.db_id || '',
              confirmed: item.matched
            });
          });
        });
        setRacingMappings(allRacingData);
      }
      
      // Initialize mappings from ALL moto sheets
      if (response.data.moto_data?.length > 0) {
        const allMotoData = [];
        response.data.moto_data.forEach(sheet => {
          sheet.data.forEach(item => {
            allMotoData.push({
              ...item,
              sheet_name: sheet.sheet_name,
              db_id: item.db_id || '',
              confirmed: item.matched
            });
          });
        });
        setMotoMappings(allMotoData);
      }

      const racingCount = response.data.racing_data?.reduce((acc, s) => acc + s.data.length, 0) || 0;
      const motoCount = response.data.moto_data?.reduce((acc, s) => acc + s.data.length, 0) || 0;
      
      if (racingCount > 0 || motoCount > 0) {
        let msg = `Excel file parsed! `;
        if (racingCount > 0) msg += `Racing: ${racingSheets.length} categories, ${racingCount} drivers. `;
        if (motoCount > 0) msg += `Moto: ${motoSheets.length} categories, ${motoCount} riders.`;
        toast.success(msg);
      }
    } catch (error) {
      toast.dismiss();
      toast.error(error.response?.data?.detail || 'Failed to parse Excel file');
    } finally {
      setUploading(false);
    }
  };

  const updateRacingMapping = (index, driverId) => {
    const driver = previewData?.available_drivers?.find(d => d.id === driverId);
    setRacingMappings(prev => prev.map((item, i) => 
      i === index ? {
        ...item,
        db_id: driverId,
        db_name: driver?.name || '',
        matched: !!driverId,
        confirmed: !!driverId
      } : item
    ));
  };

  const updateMotoMapping = (index, riderId) => {
    const rider = previewData?.available_riders?.find(r => r.id === riderId);
    setMotoMappings(prev => prev.map((item, i) => 
      i === index ? {
        ...item,
        db_id: riderId,
        db_name: rider?.name || '',
        matched: !!riderId,
        confirmed: !!riderId
      } : item
    ));
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/results/import`, {
        racing_mappings: racingMappings.filter(m => m.db_id),
        moto_mappings: motoMappings.filter(m => m.db_id)
      }, { withCredentials: true });

      toast.success(response.data.message);
      
      if (response.data.errors?.length > 0) {
        response.data.errors.forEach(err => toast.error(err));
      }

      // Refresh current results
      fetchCurrentResults();
      
      // Clear preview
      setPreviewData(null);
      setRacingMappings([]);
      setMotoMappings([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import results');
    } finally {
      setImporting(false);
    }
  };

  const handleClearResults = async (league) => {
    if (!window.confirm(`Are you sure you want to clear all ${league} results?`)) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/results/clear?league=${league}`, { withCredentials: true });
      toast.success(`Cleared ${league} results`);
      fetchCurrentResults();
    } catch (error) {
      toast.error('Failed to clear results');
    }
  };

  const unmatchedRacing = racingMappings.filter(m => !m.db_id).length;
  const unmatchedMoto = motoMappings.filter(m => !m.db_id).length;

  return (
    <div className="space-y-6">
      {/* Upload Section */}
      <div className="bg-card border border-zinc-800 p-6">
        <h2 className="font-heading font-bold text-2xl mb-4 flex items-center">
          <FileSpreadsheet className="mr-2 text-primary" size={24} />
          Import Results from File
        </h2>
        
        <p className="text-zinc-400 mb-4">
          Upload an Excel file (.xlsx, .xls) or PDF with race results. The file should have separate sheets/pages for Racing and Moto leagues.
          <br />
          <span className="text-zinc-500 text-sm">Expected columns: No. | Name | Tuner | Model/Engine | Round 1 | Round 2 | ... | Overall points</span>
        </p>

        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center space-x-2 bg-primary hover:bg-primary/80 text-white px-6 py-3 cursor-pointer transition-colors font-bold">
            <Upload size={20} />
            <span>{uploading ? 'Uploading...' : 'Upload Excel File'}</span>
            <input
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={handleFileUpload}
              disabled={uploading}
              data-testid="xlsx-upload-input"
            />
          </label>

          <label className="flex items-center space-x-2 bg-red-600 hover:bg-red-500 text-white px-6 py-3 cursor-pointer transition-colors font-bold">
            <FileText size={20} />
            <span>{uploading ? 'Uploading...' : 'Upload PDF'}</span>
            <input
              type="file"
              accept=".pdf"
              className="hidden"
              onChange={handleFileUpload}
              disabled={uploading}
              data-testid="pdf-upload-input"
            />
          </label>

          <button
            onClick={handleDownloadTemplate}
            className="flex items-center space-x-2 bg-zinc-700 hover:bg-zinc-600 text-white px-6 py-3 transition-colors font-bold"
            data-testid="download-template-button"
          >
            <Download size={20} />
            <span>Download Template</span>
          </button>

          {previewData && (
            <span className="text-secondary">
              Found {previewData.sheets?.length || 0} {previewData.file_type === 'pdf' ? 'page(s)' : 'sheet(s)'}
            </span>
          )}
        </div>
      </div>

      {/* Debug Info Section - shown when no data found */}
      {previewData && previewData.debug_info?.length > 0 && racingMappings.length === 0 && motoMappings.length === 0 && (
        <div className="bg-yellow-900/20 border border-yellow-700 p-6">
          <h3 className="font-heading font-bold text-xl text-yellow-500 mb-4 flex items-center">
            <AlertTriangle className="mr-2" size={20} />
            Debug Information
          </h3>
          <p className="text-zinc-300 mb-4">
            The parser couldn't find valid data in your file. Here's what was detected:
          </p>
          <div className="space-y-3">
            {previewData.debug_info.map((debug, idx) => (
              <div key={debug.sheet || `debug-${idx}`} className="bg-zinc-900 border border-zinc-700 p-4">
                <p className="font-bold text-white">Sheet: {debug.sheet}</p>
                <p className="text-sm text-zinc-400">
                  Status: <span className={debug.status === 'OK' ? 'text-green-500' : 'text-yellow-500'}>{debug.status}</span>
                </p>
                <p className="text-sm text-zinc-400">
                  Header row found: {debug.header_row_found || 'Not found'}
                </p>
                {debug.headers_detected?.length > 0 && (
                  <p className="text-sm text-zinc-400">
                    Detected headers: {debug.headers_detected.filter(h => h).join(' | ')}
                  </p>
                )}
                <p className="text-sm text-zinc-400">
                  Name column: {debug.name_col_index !== null ? `Column ${debug.name_col_index + 1}` : 'Not found'}
                </p>
              </div>
            ))}
          </div>
          <p className="text-zinc-400 mt-4 text-sm">
            <strong>Tip:</strong> Make sure your file has a column with header "Name" (or similar like "Driver", "Rider", "Participant") 
            in the first 20 rows, and a "Points" or "Overall" column.
          </p>
        </div>
      )}

      {/* Preview and Mapping Section */}
      {previewData && (racingMappings.length > 0 || motoMappings.length > 0) && (
        <div className="bg-card border border-zinc-800 p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-heading font-bold text-xl">Review & Map Results</h3>
            <div className="flex items-center space-x-4">
              {(unmatchedRacing > 0 || unmatchedMoto > 0) && (
                <span className="text-yellow-500 flex items-center">
                  <AlertTriangle size={16} className="mr-1" />
                  {unmatchedRacing + unmatchedMoto} unmatched entries
                </span>
              )}
              <button
                onClick={handleImport}
                disabled={importing || (racingMappings.length === 0 && motoMappings.length === 0)}
                className="flex items-center space-x-2 bg-secondary hover:bg-secondary/80 text-black px-6 py-2 font-bold transition-colors disabled:opacity-50"
                data-testid="import-results-button"
              >
                <Check size={18} />
                <span>{importing ? 'Importing...' : 'Import All Results'}</span>
              </button>
            </div>
          </div>

          {/* League Tabs */}
          <div className="flex space-x-2 mb-4">
            <button
              onClick={() => { 
                setActiveLeague('racing'); 
                setSelectedSheet('all');
                // Update sheet list to racing sheets
                const racingSheets = previewData?.racing_data?.map(s => s.sheet_name) || [];
                setAllSheets(racingSheets);
              }}
              className={`px-4 py-2 font-bold transition-colors ${
                activeLeague === 'racing'
                  ? 'bg-primary text-white'
                  : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
              }`}
            >
              Racing ({racingMappings.length})
            </button>
            <button
              onClick={() => { 
                setActiveLeague('moto'); 
                setSelectedSheet('all');
                // Update sheet list to moto sheets
                const motoSheets = previewData?.moto_data?.map(s => s.sheet_name) || [];
                setAllSheets(motoSheets);
              }}
              className={`px-4 py-2 font-bold transition-colors ${
                activeLeague === 'moto'
                  ? 'bg-secondary text-black'
                  : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
              }`}
            >
              Moto ({motoMappings.length})
            </button>
          </div>

          {/* Sheet/Category Filter - Works for both Racing and Moto */}
          {allSheets.length > 0 && (
            <div className="mb-4">
              <label className="text-sm text-zinc-400 mr-2">Filter by Category:</label>
              <select
                value={selectedSheet}
                onChange={(e) => setSelectedSheet(e.target.value)}
                className="bg-zinc-900 border border-zinc-700 px-3 py-2 text-white"
              >
                <option value="all">
                  All Categories ({activeLeague === 'racing' ? racingMappings.length : motoMappings.length} {activeLeague === 'racing' ? 'drivers' : 'riders'})
                </option>
                {allSheets.map(sheet => {
                  const mappings = activeLeague === 'racing' ? racingMappings : motoMappings;
                  const count = mappings.filter(m => m.sheet_name === sheet).length;
                  return (
                    <option key={sheet} value={sheet}>{sheet} ({count} {activeLeague === 'racing' ? 'drivers' : 'riders'})</option>
                  );
                })}
              </select>
            </div>
          )}

          {/* Racing Mappings */}
          {activeLeague === 'racing' && racingMappings.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-700">
                    <th className="text-left p-3 text-zinc-400 text-sm">Status</th>
                    <th className="text-left p-3 text-zinc-400 text-sm">Category</th>
                    <th className="text-left p-3 text-zinc-400 text-sm">Driver Name</th>
                    <th className="text-left p-3 text-zinc-400 text-sm">Tuner</th>
                    <th className="text-left p-3 text-zinc-400 text-sm">Map to Driver</th>
                    <th className="text-right p-3 text-zinc-400 text-sm">Points</th>
                  </tr>
                </thead>
                <tbody>
                  {racingMappings
                    .filter(item => selectedSheet === 'all' || item.sheet_name === selectedSheet)
                    .map((item, index) => {
                      // Find the actual index in the full array for updating
                      const actualIndex = racingMappings.findIndex(m => m === item);
                      return (
                        <tr key={actualIndex} className={`border-b border-zinc-800 ${!item.db_id ? 'bg-yellow-900/10' : ''}`}>
                          <td className="p-3">
                            {item.db_id ? (
                              <Check className="text-green-500" size={18} />
                            ) : (
                              <AlertTriangle className="text-yellow-500" size={18} />
                            )}
                          </td>
                          <td className="p-3 text-xs text-zinc-500">{item.sheet_name}</td>
                          <td className="p-3">
                            <span className="font-medium">{item.xlsx_name}</span>
                            {item.is_co_driver && (
                              <span className="ml-2 text-xs bg-blue-900/50 text-blue-300 px-2 py-0.5 rounded">
                                <Users size={10} className="inline mr-1" />
                                Co-Driver
                              </span>
                            )}
                            {item.xlsx_original_name && (
                              <p className="text-xs text-zinc-500 mt-1">From: {item.xlsx_original_name}</p>
                            )}
                          </td>
                          <td className="p-3 text-zinc-400">{item.xlsx_tuner}</td>
                          <td className="p-3">
                            <select
                              value={item.db_id || ''}
                              onChange={(e) => updateRacingMapping(actualIndex, e.target.value)}
                              className="bg-zinc-900 border border-zinc-700 px-3 py-2 text-white w-full"
                              data-testid={`racing-mapping-${actualIndex}`}
                            >
                              <option value="">-- Select Driver --</option>
                              {previewData.available_drivers?.map(driver => (
                                <option key={driver.id} value={driver.id}>
                                  {driver.name} ({driver.tuner})
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="p-3 text-right font-mono text-primary font-bold">
                            {item.overall_points}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}

          {/* Moto Mappings */}
          {activeLeague === 'moto' && motoMappings.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-700">
                    <th className="text-left p-3 text-zinc-400 text-sm">Status</th>
                    <th className="text-left p-3 text-zinc-400 text-sm">Category</th>
                    <th className="text-left p-3 text-zinc-400 text-sm">Rider Name</th>
                    <th className="text-left p-3 text-zinc-400 text-sm">Team</th>
                    <th className="text-left p-3 text-zinc-400 text-sm">Map to Rider</th>
                    <th className="text-right p-3 text-zinc-400 text-sm">Points</th>
                  </tr>
                </thead>
                <tbody>
                  {motoMappings
                    .filter(item => selectedSheet === 'all' || item.sheet_name === selectedSheet)
                    .map((item, index) => {
                      const actualIndex = motoMappings.findIndex(m => m === item);
                      return (
                        <tr key={actualIndex} className={`border-b border-zinc-800 ${!item.db_id ? 'bg-yellow-900/10' : ''}`}>
                          <td className="p-3">
                            {item.db_id ? (
                              <Check className="text-green-500" size={18} />
                            ) : (
                              <AlertTriangle className="text-yellow-500" size={18} />
                            )}
                          </td>
                          <td className="p-3 text-xs text-zinc-500">{item.sheet_name}</td>
                          <td className="p-3">
                            <span className="font-medium">{item.xlsx_name}</span>
                            {item.is_co_driver && (
                              <span className="ml-2 text-xs bg-green-900/50 text-green-300 px-2 py-0.5 rounded">
                                <Users size={10} className="inline mr-1" />
                                Co-Rider
                              </span>
                            )}
                            {item.xlsx_original_name && (
                              <p className="text-xs text-zinc-500 mt-1">From: {item.xlsx_original_name}</p>
                            )}
                          </td>
                          <td className="p-3 text-zinc-400">{item.xlsx_tuner}</td>
                          <td className="p-3">
                            <select
                              value={item.db_id || ''}
                              onChange={(e) => updateMotoMapping(actualIndex, e.target.value)}
                              className="bg-zinc-900 border border-zinc-700 px-3 py-2 text-white w-full"
                              data-testid={`moto-mapping-${actualIndex}`}
                            >
                              <option value="">-- Select Rider --</option>
                              {previewData.available_riders?.map(rider => (
                                <option key={rider.id} value={rider.id}>
                                  {rider.name} ({rider.tuner})
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="p-3 text-right font-mono text-secondary font-bold">
                            {item.overall_points}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}

          {activeLeague === 'racing' && racingMappings.length === 0 && (
            <p className="text-zinc-500 text-center py-8">No racing data found in the uploaded file</p>
          )}
          {activeLeague === 'moto' && motoMappings.length === 0 && (
            <p className="text-zinc-500 text-center py-8">No moto data found in the uploaded file</p>
          )}
        </div>
      )}

      {/* Current Results Section */}
      <div className="bg-card border border-zinc-800 p-6">
        <button
          onClick={() => setShowCurrentResults(!showCurrentResults)}
          className="flex items-center justify-between w-full"
        >
          <h3 className="font-heading font-bold text-xl flex items-center">
            <RefreshCw className="mr-2" size={20} />
            Current Imported Results
          </h3>
          {showCurrentResults ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>

        {showCurrentResults && currentResults && (
          <div className="mt-6 space-y-6">
            {/* Racing Results */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <h4 className="font-heading font-bold text-lg text-primary">Racing Results ({currentResults.racing_results?.length || 0})</h4>
                {currentResults.racing_results?.length > 0 && (
                  <button
                    onClick={() => handleClearResults('racing')}
                    className="text-red-500 hover:text-red-400 flex items-center text-sm"
                  >
                    <Trash2 size={14} className="mr-1" />
                    Clear Racing
                  </button>
                )}
              </div>
              {currentResults.racing_results?.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {currentResults.racing_results.map(result => (
                    <div key={result.driver_id} className="bg-zinc-900 border border-zinc-800 p-3">
                      <p className="font-bold text-sm">{result.db_name}</p>
                      <p className="text-xs text-zinc-500">{result.xlsx_name}</p>
                      <p className="font-mono text-primary font-bold">{result.total_points} pts</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-zinc-500">No racing results imported yet</p>
              )}
            </div>

            {/* Moto Results */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <h4 className="font-heading font-bold text-lg text-secondary">Moto Results ({currentResults.moto_results?.length || 0})</h4>
                {currentResults.moto_results?.length > 0 && (
                  <button
                    onClick={() => handleClearResults('moto')}
                    className="text-red-500 hover:text-red-400 flex items-center text-sm"
                  >
                    <Trash2 size={14} className="mr-1" />
                    Clear Moto
                  </button>
                )}
              </div>
              {currentResults.moto_results?.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {currentResults.moto_results.map(result => (
                    <div key={result.rider_id} className="bg-zinc-900 border border-zinc-800 p-3">
                      <p className="font-bold text-sm">{result.db_name}</p>
                      <p className="text-xs text-zinc-500">{result.xlsx_name}</p>
                      <p className="font-mono text-secondary font-bold">{result.total_points} pts</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-zinc-500">No moto results imported yet</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
